import { userContext } from 'components/contexts/auth/auth.context';
import { environment } from 'env/environment';
import { IUploadedFile, UploaderFileTypes } from 'interfaces/formsComponents.interface';
import React from 'react';
import { gateway, getSID } from 'helpers/gateway-helper';
import postFailUpload from '@anghami/neogateway/dist/endpoints/postFailUpload';
import postVerifySession from '@anghami/neogateway/dist/endpoints/postVerifySession';
import validateUploadSize from '@anghami/neogateway/dist/endpoints/validateUploadSize';
import { createFile, generateKey } from 'helpers/file-helper';
import { logGeneralErrorEvent } from 'helpers/analytics-helper';

interface IUploadFileHookProps {
  fileType: UploaderFileTypes;
  maxSize?: number;
  onSuccess: (uploadedFile: IUploadedFile) => void;
  onError: (err: any) => void;
  initialFile?: IUploadedFile;
}
export interface IUseFileUploaderReturnType {
  uploadFile: (uploadedFile: IUploadedFile) => void;
  clearUploadedFile: () => void;
  uploadedFile: IUploadedFile;
  loadingProgress: number;
  isLoading: boolean;
  isUploadSuccess: boolean;
  isValidating: boolean;
}

const DEFAULT_IMAGE_MAX_SIZE = 3000000; // 3MB, applies to all image uploads unless overridden

// PRESIGNED_CHUNK_SIZE matches dashboard's fixed chunk size, kept consistent
// across clients uploading to the same backend.
const PRESIGNED_CHUNK_SIZE = 50000000; // 50MB
const PRESIGNED_BASE = 'https://tusk.anghami.com/s3/';
const PART_RETRY_LIMIT = 3;

interface IAbortState {
  cancelled: boolean;
  activeXhrs: Set<XMLHttpRequest>;
}

const newAbortState = (): IAbortState => ({ cancelled: false, activeXhrs: new Set() });

const abortActiveParts = (abortState: IAbortState) => {
  abortState.activeXhrs.forEach((xhr) => xhr.abort());
  abortState.activeXhrs.clear();
};

const cancelUpload = (abortState: IAbortState) => {
  abortState.cancelled = true;
  abortActiveParts(abortState);
};

const throwIfCancelled = (abortState: IAbortState) => {
  if (abortState.cancelled) {
    throw new Error('cancelled');
  }
};

const REAUTH_TIMEOUT_MS = 30000;

// Parts upload concurrently (not one at a time) so the whole session finishes
// faster - a long sequential chain of many parts is exactly what stretches an
// upload long enough to hit a stale/dropped connection before `complete` is
// ever called, after every part has already succeeded. A moderate cap keeps
// this from saturating the user's own uplink and causing the same problem by
// a different route.
const CONCURRENT_PART_UPLOADS = 4;

async function uploadPartsConcurrently<T>(
  items: number[],
  worker: (item: number) => Promise<T>,
  onFirstFailure: () => void
): Promise<T[]> {
  const results: T[] = new Array(items.length);
  let nextIndex = 0;
  let failed = false;
  async function runWorker() {
    while (!failed && nextIndex < items.length) {
      const currentIndex = nextIndex++;
      try {
        results[currentIndex] = await worker(items[currentIndex]);
      } catch (error) {
        if (!failed) {
          failed = true;
          onFirstFailure();
        }
        throw error;
      }
    }
  }
  const workerCount = Math.min(CONCURRENT_PART_UPLOADS, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
  return results;
}

const JSON_REQUEST_RETRY_DELAYS = [1000, 3000, 5000]; // create/sign-parts/complete/abort

class PresignedRequestError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
  }
}

const isClientRejection = (error: any) => error?.status >= 400 && error?.status < 500;

// Retries transient network failures (e.g. a connection going stale across a
// long multi-part upload and dropping right as `complete` is called, after
// every part already succeeded) and 5xx responses. Does not retry 4xx - those
// are real rejections (bad request, auth, disallowed bucket) that won't
// change on retry.
async function presignedJsonRequest(path: string, body: unknown, attempt = 0): Promise<any> {
  let response: Response;
  try {
    response = await fetch(PRESIGNED_BASE + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch (networkError) {
    if (attempt < JSON_REQUEST_RETRY_DELAYS.length) {
      await new Promise((resolve) => setTimeout(resolve, JSON_REQUEST_RETRY_DELAYS[attempt]));
      return presignedJsonRequest(path, body, attempt + 1);
    }
    throw new PresignedRequestError(`${path} network error after ${attempt} retries`);
  }

  if (!response.ok) {
    if (response.status >= 500 && attempt < JSON_REQUEST_RETRY_DELAYS.length) {
      await new Promise((resolve) => setTimeout(resolve, JSON_REQUEST_RETRY_DELAYS[attempt]));
      return presignedJsonRequest(path, body, attempt + 1);
    }
    throw new PresignedRequestError(
      `${path} failed with status ${response.status}: ${await response.text()}`,
      response.status
    );
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

function putPartWithRetry(
  url: string,
  blob: Blob,
  onPartProgress: (uploadedBytes: number) => void,
  abortState: IAbortState,
  attempt = 0
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (abortState.cancelled) {
      reject(new Error('cancelled'));
      return;
    }
    const xhr = new XMLHttpRequest();
    abortState.activeXhrs.add(xhr);
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onPartProgress(e.loaded);
      }
    };
    xhr.onload = () => {
      abortState.activeXhrs.delete(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag');
        resolve(etag ? etag.replace(/"/g, '') : '');
      } else {
        onPartFailed();
      }
    };
    xhr.onerror = () => {
      abortState.activeXhrs.delete(xhr);
      onPartFailed();
    };
    xhr.onabort = () => {
      abortState.activeXhrs.delete(xhr);
      reject(new Error('cancelled'));
    };
    function onPartFailed() {
      if (abortState.cancelled) {
        reject(new Error('cancelled'));
        return;
      }
      if (attempt < PART_RETRY_LIMIT) {
        onPartProgress(0);
        resolve(putPartWithRetry(url, blob, onPartProgress, abortState, attempt + 1));
      } else {
        reject(new Error(`part upload failed after ${PART_RETRY_LIMIT} retries`));
      }
    }
    xhr.send(blob);
  });
}

function useFileUploader({
  fileType,
  maxSize,
  onSuccess,
  onError,
  initialFile
}: IUploadFileHookProps): IUseFileUploaderReturnType {
  const [uploadedFile, setUploadedFile] = React.useState<IUploadedFile>(initialFile || null);
  const [loadingProgress, setLoadingProgress] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const { user, reauth } = React.useContext(userContext);
  const keyRef = React.useRef(null);
  const timeout = null;
  const abortStateRef = React.useRef<IAbortState>(newAbortState());
  const uploadedFileRef = React.useRef<IUploadedFile>(uploadedFile);
  uploadedFileRef.current = uploadedFile;

  const validateFileUploaded = async (
    key: string,
    abortState: IAbortState,
    retryTimes = 0
  ): Promise<boolean> => {
    if (abortState.cancelled) {
      return false;
    }
    try {
      const { data } = await gateway.callEndpoint(validateUploadSize, {
        key,
        bucket: environment.upload_s3_bucket,
        output: 'jsonhp'
      });

      if (!data || data.error) {
        if (retryTimes < 10) {
          let delay = 2000;
          if (uploadedFileRef.current) {
            if (uploadedFileRef.current.size > 500000000) {
              delay = [5000, 10000, 15000, 20000, 25000, 30000, 30000, 30000, 30000, 30000][
                retryTimes
              ];
            } else if (uploadedFileRef.current.size > 100000000) {
              delay = [3000, 5000, 8000, 10000, 15000, 20000, 25000, 30000, 30000, 30000][
                retryTimes
              ];
            } else {
              delay = [2000, 3000, 5000, 7000, 10000, 15000, 20000, 25000, 30000, 30000][
                retryTimes
              ];
            }
          }

          await new Promise((resolve) => setTimeout(resolve, delay));
          return validateFileUploaded(key, abortState, retryTimes + 1);
        }
        logError(new Error(data?.error?.message || 'Failed to validate file (key not on s3)'));
        return false;
      }
      return true;
    } catch (error) {
      if (retryTimes < 5) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return validateFileUploaded(key, abortState, retryTimes + 1);
      }
      logError(error);
      return false;
    }
  };

  const handleUploadSuccess = async (
    key: string,
    abortState: IAbortState,
    completeError: Error | null
  ) => {
    if (!key) {
      throw new Error('failed to save key');
    }

    setIsValidating(true);
    setIsLoading(false);
    setLoadingProgress(100);

    if (uploadedFileRef.current && uploadedFileRef.current.size > 100000000) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }

    const isValidated = await validateFileUploaded(key, abortState);
    throwIfCancelled(abortState);

    setIsValidating(false);

    if (!isValidated) {
      throw completeError || new Error('Failed to validate uploaded file on S3');
    }

    setIsUploadSuccess(true);
    onSuccess({ ...uploadedFileRef.current, isUploaded: true, key });
  };
  const handleUploadError = (err) => {
    setIsLoading(false);
    setIsValidating(false);
    setIsUploadSuccess(false);
    setUploadedFile((prev) => (prev ? { ...prev, isUploaded: false } : prev));
    gateway.callEndpoint(postFailUpload, {
      reason: err.message,
      timestamp: Date.now(),
      feature: 'new creators'
    });
    logError(err);
    onError(err);
  };

  React.useEffect(() => {
    if (!initialFile) return;
    setUploadedFile(initialFile);
    setIsUploadSuccess(!!initialFile?.key);
  }, [initialFile]);

  React.useEffect(() => {
    const callPostVerifySession = async () => {
      const { data } = await gateway.callEndpoint(postVerifySession, {
        validatesid: getSID(),
        output: 'jsonhp'
      });
      if (data?.error && data.error.code === 401) {
        reauth();
      }
    };
    callPostVerifySession();
    return () => clearTimeout(timeout);
  }, []);

  // reauth only calls back on success, after it has written the new session cookie that getSID reads.
  const reauthenticate = () =>
    new Promise<string>((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error('reauthentication timed out')),
        REAUTH_TIMEOUT_MS
      );
      reauth(() => {
        clearTimeout(timer);
        resolve(getSID());
      });
    });

  const runUpload = async (
    uf: IUploadedFile,
    file: Blob,
    proposedKey: string,
    abortState: IAbortState,
    sessionToken: string
  ) => {
    const bucket = environment.upload_s3_bucket;
    const created = await presignedJsonRequest('create', {
      filename: proposedKey,
      bucket,
      contentType: uf.type,
      token: sessionToken || environment.general_s3_token
    });

    let completed = false;
    try {
      throwIfCancelled(abortState);
      // The server inserts a uniqueness suffix into the key, so everything downstream uses its key.
      keyRef.current = created.key;
      setUploadedFile((prev) => (prev ? { ...prev, key: created.key } : prev));

      const numParts = Math.max(1, Math.ceil(file.size / PRESIGNED_CHUNK_SIZE));
      const partNumbers = Array.from({ length: numParts }, (_, i) => i + 1);

      const signed = await presignedJsonRequest('sign-parts', {
        bucket,
        key: created.key,
        uploadId: created.uploadId,
        partNumbers
      });
      throwIfCancelled(abortState);
      const urlByPart = new Map<number, string>(
        signed.parts.map((p: { partNumber: number; url: string }) => [p.partNumber, p.url])
      );

      const partBytesUploaded = new Array(numParts + 1).fill(0);
      let maxUploadedBytes = 0;
      const reportProgress = () => {
        const uploaded = partBytesUploaded.reduce((a, b) => a + b, 0);
        maxUploadedBytes = Math.max(maxUploadedBytes, uploaded);
        setLoadingProgress(Math.round((maxUploadedBytes / file.size) * 100));
      };

      const completedParts = await uploadPartsConcurrently(
        partNumbers,
        async (partNumber) => {
          throwIfCancelled(abortState);
          const start = (partNumber - 1) * PRESIGNED_CHUNK_SIZE;
          const end = Math.min(start + PRESIGNED_CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          const etag = await putPartWithRetry(
            urlByPart.get(partNumber),
            blob,
            (uploadedBytes) => {
              partBytesUploaded[partNumber] = uploadedBytes;
              reportProgress();
            },
            abortState
          );
          return { partNumber, etag };
        },
        () => abortActiveParts(abortState)
      );

      let completeError: Error | null = null;
      try {
        await presignedJsonRequest('complete', {
          bucket,
          key: created.key,
          uploadId: created.uploadId,
          parts: completedParts
        });
        completed = true;
      } catch (error: any) {
        // S3 can finish before the connection drops, so a retried complete fails on the gone uploadId; let validation decide.
        if (isClientRejection(error)) {
          throw error;
        }
        completeError = error;
      }
      throwIfCancelled(abortState);

      await handleUploadSuccess(created.key, abortState, completeError);
      completed = true;
    } catch (error) {
      if (!completed) {
        presignedJsonRequest('abort', {
          bucket,
          key: created.key,
          uploadId: created.uploadId
        }).catch(() => undefined);
      }
      throw error;
    }
  };

  const uploadFile = async (uf: IUploadedFile) => {
    const effectiveMaxSize =
      maxSize ?? (fileType === UploaderFileTypes.image ? DEFAULT_IMAGE_MAX_SIZE : undefined);
    if (effectiveMaxSize && uf.size > effectiveMaxSize) {
      onError({ message: 'Image size must be less than ' + effectiveMaxSize / 1000000 + 'MB' });
      return;
    }

    cancelUpload(abortStateRef.current);
    const abortState = newAbortState();
    abortStateRef.current = abortState;

    try {
      const proposedKey = generateKey(user?.anid || 'anon', uf, fileType);
      keyRef.current = proposedKey;
      setUploadedFile({ ...uf, key: proposedKey });
      setIsLoading(true);
      setLoadingProgress(0);
      const file = uf.file ? uf.file : await createFile(uf);
      if (!file) return;

      try {
        await runUpload(uf, file, proposedKey, abortState, user?.socketsessionid);
      } catch (error: any) {
        if (abortState.cancelled || error?.status !== 401) {
          throw error;
        }
        const freshToken = await reauthenticate();
        throwIfCancelled(abortState);
        await runUpload(uf, file, proposedKey, abortState, freshToken);
      }
    } catch (error: any) {
      if (abortState.cancelled || error?.message === 'cancelled') {
        return;
      }
      console.error('🔴 ~ Error uploading file', error);
      handleUploadError(error);
    }
  };

  const logError = (error) => {
    logGeneralErrorEvent(error, 'useFileUploader', {
      error: 'validateUploadSize failed',
      message: error.message,
      key: keyRef.current,
      file: JSON.stringify(uploadedFileRef.current)
    });
  };

  const clearUploadedFile = () => {
    cancelUpload(abortStateRef.current);
    if (uploadedFile?.src) {
      URL.revokeObjectURL(uploadedFile?.src);
    }
    setUploadedFile(null);
    setLoadingProgress(0);
    setIsLoading(false);
    setIsValidating(false);
    setIsUploadSuccess(false);
  };

  return {
    uploadFile,
    clearUploadedFile,
    uploadedFile,
    loadingProgress,
    isLoading,
    isUploadSuccess,
    isValidating
  };
}

export default useFileUploader;
